import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const FreelanceHourlyRate = () => {
  // State variables for Freelance Hourly Rate calculation
  const [annualIncome, setAnnualIncome] = useState(0); // Desired annual income
  const [expenses, setExpenses] = useState(0); // Business expenses
  const [taxes, setTaxes] = useState(0); // Estimated taxes
  const [billableHours, setBillableHours] = useState(0); // Total billable hours worked in a year
  const [hourlyRate, setHourlyRate] = useState(0); // Calculated hourly rate

  // Event handlers to update state values
  const handleAnnualIncomeChange = (e) =>
    setAnnualIncome(parseFloat(e.target.value) || 0);
  const handleExpensesChange = (e) =>
    setExpenses(parseFloat(e.target.value) || 0);
  const handleTaxesChange = (e) => setTaxes(parseFloat(e.target.value) || 0);
  const handleBillableHoursChange = (e) =>
    setBillableHours(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate Freelance Hourly Rate when any input changes
  useEffect(() => {
    if (billableHours > 0) {
      const totalIncomeNeeded = annualIncome + expenses + taxes; // Total income needed to cover expenses and taxes
      const calculatedRate = totalIncomeNeeded / billableHours;
      setHourlyRate(calculatedRate);
    } else {
      setHourlyRate(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [annualIncome, expenses, taxes, billableHours]);

  return (
    <Layout
      title="Freelance Hourly Rate Calculator"
      path="freelance-hourly-rate"
    >
      <section
        className="section-padding"
        id="freelance-hourly-rate-calculator"
      >
        <div className="container mt-5">
          <div className="row">
            {/* Freelance Hourly Rate Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">
                  Freelance Hourly Rate Calculator
                </h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Annual Income Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="annualIncome" className="form-label">
                        Desired Annual Income (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Desired Annual Income:</strong> The amount you want to earn in a year after business expenses and taxes."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="annualIncome"
                        placeholder="Enter desired annual income"
                        value={annualIncome || ""}
                        onChange={handleAnnualIncomeChange}
                      />
                    </div>

                    {/* Expenses Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="expenses" className="form-label">
                        Business Expenses (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Business Expenses:</strong> All expenses related to running your freelance business, such as equipment, software, advertising, and office supplies."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="expenses"
                        placeholder="Enter business expenses"
                        value={expenses || ""}
                        onChange={handleExpensesChange}
                      />
                    </div>

                    {/* Taxes Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="taxes" className="form-label">
                        Estimated Taxes (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Estimated Taxes:</strong> The estimated tax amount you expect to pay after accounting for your income, expenses, and deductions."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="taxes"
                        placeholder="Enter estimated taxes"
                        value={taxes || ""}
                        onChange={handleTaxesChange}
                      />
                    </div>

                    {/* Billable Hours Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="billableHours" className="form-label">
                        Billable Hours per Year
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Billable Hours:</strong> The total number of hours you plan to bill clients in a year. This should exclude holidays, vacations, and non-billable activities."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="billableHours"
                        placeholder="Enter billable hours per year"
                        value={billableHours || ""}
                        onChange={handleBillableHoursChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Required Hourly Rate:{" "}
                        <span className="fw-bold ms-3">
                          {hourlyRate === 0
                            ? "0.00"
                            : hourlyRate.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                          ₹
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Freelance Hourly Rate Calculator
                </h3>

                {/* What is a Freelance Hourly Rate Calculator? */}
                <h4 className="mt-3 mb-3">
                  What is a Freelance Hourly Rate Calculator?
                </h4>
                <p>
                  A **Freelance Hourly Rate Calculator** helps freelancers and
                  self-employed professionals estimate the hourly rate they need
                  to charge to meet their income goals. It takes into account
                  desired annual income, business expenses, taxes, and billable
                  hours.
                </p>

                {/* Why Use a Freelance Hourly Rate Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use a Freelance Hourly Rate Calculator?
                </h4>
                <p>
                  Freelancers often struggle with determining how much they
                  should charge per hour to cover their expenses and meet their
                  financial goals. This calculator provides a simple way to
                  determine a fair hourly rate that ensures a profitable
                  business.
                </p>

                {/* Benefits of Using a Freelance Hourly Rate Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using a Freelance Hourly Rate Calculator
                </h4>
                <ul>
                  <li>
                    <strong>
                      Supports Transparent and Profitable Pricing:
                    </strong>{" "}
                    Helps freelancers set prices that reflect both their
                    business costs and income goals.
                  </li>
                  <li>
                    <strong>Helps Freelancers Set Competitive Rates:</strong>{" "}
                    Provides a foundation for setting rates that ensure
                    financial sustainability and competitiveness in the market.
                  </li>
                  <li>
                    <strong>Considers Income Goals and Work Hours:</strong>{" "}
                    Takes into account your desired income and the hours you
                    plan to work, offering a customized pricing strategy.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use a Freelance Hourly Rate Calculator?
                </h4>
                <p>The Freelance Hourly Rate Calculator is ideal for:</p>
                <ul>
                  <li>
                    <strong>Freelancers:</strong> Helps freelancers calculate
                    the rates they should charge to meet their income and
                    financial goals.
                  </li>
                  <li>
                    <strong>Consultants:</strong> Useful for consultants to
                    determine competitive rates that align with their business
                    needs.
                  </li>
                  <li>
                    <strong>Self-Employed Professionals:</strong> Provides
                    clarity on pricing for anyone working independently in
                    fields such as writing, design, or programming.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">
                  How do I determine my billable hours?
                </h4>
                <p>
                  Billable hours are the hours you can charge clients for the
                  work you do. This excludes non-billable time such as
                  administrative tasks, meetings, and breaks. Estimate the
                  number of hours you can realistically work on client projects
                  in a year.
                </p>

                <h4 className="mt-4 mb-3">
                  Why should I include taxes and expenses in my rate?
                </h4>
                <p>
                  Including taxes and business expenses ensures that you are not
                  just covering your income goals but also the costs associated
                  with running your freelance business. This ensures
                  profitability and sustainability in the long term.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default FreelanceHourlyRate;
